import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FilterValue } from '../grid-filter/utils/filter-value.interface';
import { TextMode } from '../../../../utils/text/text-mode.enum';
import { TagType } from '../../../../utils/tag/tag-type.enum';
import { CrudViewColumnType } from '../../../../utils/list-grid/crud-view-column-type.enum';

@Component({
  selector: 'rappider-filter-section',
  templateUrl: './filter-section.component.html',
  styleUrls: ['./filter-section.component.scss']
})
export class RappiderFilterSectionComponent {

  @Input() hasActiveFilters: boolean;
  @Input() appliedFilters: { [key: string]: FilterValue } = {};
  @Input() config: any; // CrudTableViewConfig

  @Output() removeFilter = new EventEmitter<string>();
  @Output() clearAllFilters = new EventEmitter<void>();

  /* Filter tag configuration */
  filterTagConfig = {
    mode: {
      closeable: TagType.Closeable,
    },
    text: {
      textMode: TextMode.Html
    },
    icon: {
      name: 'fa-solid fa-times-circle',
      type: 'font-awesome',
      color: '#ff4d4f'
    }
  };

  /* Clear all filters configuration */
  clearAllFiltersConfig = {
    text: 'Clear All Filters',
    size: 'extra-small'
  };

  getAppliedFiltersKeys(): string[] {
    return Object.keys(this.appliedFilters);
  }

  getColumnTitle(fieldName: string): string {
    const column = this.config?.columns?.find(col => col.fieldName === fieldName);
    return column?.title || fieldName;
  }

  getFilterConditionText(filterValue: FilterValue): string {
    if (!filterValue) {
      return '';
    }

    if (filterValue.condition === 'contains') {
      return 'contains';
    }
    if (filterValue.condition === 'equals') {
      return 'is-equal-to';
    }
    if (filterValue.condition === 'startsWith') {
      return 'starts-with';
    }
    if (filterValue.condition === 'endsWith') {
      return 'ends-with';
    }
    if (filterValue.condition === 'isEmpty') {
      return 'is-empty';
    }
    if (filterValue.condition === 'isNotEmpty') {
      return 'is-not-empty';
    }
    return filterValue.condition;
  }

  getFilterTagContent(fieldName: string): string {
    const filter = this.appliedFilters[fieldName];
    if (!filter) {
      return 'Filter';
    }

    const columnTitle = this.getColumnTitle(fieldName);
    const condition = this.getFilterConditionText(filter);
    const value = filter.filterValue;

    if (value !== null && value !== undefined && value !== '') {
      const formattedValue = this.formatFilterValue(fieldName, value);
      return `<span style="font-weight: 700;">${columnTitle}</span>: <span style="font-weight: 500;">${condition}</span> "<span style="font-weight: 400;">${formattedValue}</span>"`;
    } else {
      return `<span style="font-weight: 700;">${columnTitle}</span>: <span style="font-weight: 500;">${condition}</span>`;
    }
  }

  formatFilterValue(fieldName: string, value: any): string {
    const column = this.config?.columns?.find(col => col.fieldName === fieldName);
    const columnType = column?.type;

    console.log('Filter formatting:', { fieldName, columnType, value, column });

    // Handle DateTime formatting for DateTime and Date columns
    if (columnType === CrudViewColumnType.DateTime || columnType === CrudViewColumnType.Date) {
      if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
        const date = new Date(value);

        // Check if it's a DateTime (has time component)
        const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;

        if (columnType === CrudViewColumnType.DateTime || hasTime) {
          // Format as DateTime: "26 May 2022, 12:00 PM"
          return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) + ', ' + date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        } else {
          // Format as Date only: "26 May 2022"
          return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
        }
      }
    }

    // Also handle any string that looks like a date (fallback for any date-like strings)
    if (typeof value === 'string' && value.includes('GMT') && !isNaN(Date.parse(value))) {
      const date = new Date(value);
      const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;

      if (hasTime) {
        return date.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }) + ', ' + date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } else {
        return date.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      }
    }

    // For other types, return as string
    return String(value);
  }

  onRemoveFilter(fieldName: string): void {
    this.removeFilter.emit(fieldName);
  }

  onClearAllFilters(): void {
    this.clearAllFilters.emit();
  }
}
